import React from 'react';
import { Box, FormControlLabel, FormGroup } from '@mui/material';
import { DayOfWeek } from '@askporter/client-grieg-lyric';
import { RoundButton, RoundButtonSize } from '../../../../Buttons/RoundButton';
import { Checkbox } from '../../../../Checkbox';
import { Dialog } from '../../../../Dialog';
import { TimePicker } from '../../../../TimePicker';
import { DayTimeRangeType } from '../../types';
import { defaultDays, days, getDays, filterNoServiceTimeDays, parseDayTime, mergeDayTimeState } from '../../utils';

export interface SelectDayTimeProps {
  title?: string;
  dataTestId?: string;
  t: (key: string, options?: Record<string, string | number>) => string;
  isLoading?: boolean;
  isVisible: boolean;
  handleClose: () => void;
  setIsModalVisible: (isModalVisible: boolean) => void;
  isSmallDevice: boolean;
  selectedDays?: Record<string, boolean>;
  setSelectedDays: (selectedDays: Record<string, boolean>) => void;
  onAdd: (value: Array<DayTimeRangeType>) => void;
  dayTimes: Record<DayOfWeek, { fromTime: string | null; toTime: string | null }>;
  addDayTime: (dayTimes: Record<DayOfWeek, { fromTime: string | null; toTime: string | null }>) => void;
  value: Array<DayTimeRangeType>;
  fromTimeValue: string | null;
  toTimeValue: string | null;
  setFromTimeValue: (fromTimeValue: string | null) => void;
  setToTimeValue: (toTimeValue: string | null) => void;
  noServiceCheckbox: boolean;
  setNoServiceCheckbox: (noServiceCheckbox: boolean) => void;
}

/**
 * Allows the user to set a time for a week day
 */
export const SelectDayTimeModal: React.FC<React.PropsWithChildren<SelectDayTimeProps>> = ({
  t,
  title,
  dataTestId,
  isLoading,
  isVisible,
  setIsModalVisible,
  handleClose,
  isSmallDevice,
  selectedDays = defaultDays,
  setSelectedDays,
  onAdd,
  value,
  dayTimes,
  addDayTime,
  fromTimeValue,
  toTimeValue,
  setFromTimeValue,
  setToTimeValue,
  noServiceCheckbox,
  setNoServiceCheckbox,
}: SelectDayTimeProps) => {
  const _onChange = (time: string | null, type: 'fromTime' | 'toTime' | 'all') => {
    if (type === 'fromTime') {
      setFromTimeValue(time ? time : null);
    } else if (type === 'toTime') {
      setToTimeValue(time ? time : null);
    } else {
      setFromTimeValue(null);
      setToTimeValue(null);
    }
  };

  const _onAdd = () => {
    const v = [
      {
        days: getDays(selectedDays),
        fromTime: fromTimeValue ? fromTimeValue : null,
        toTime: toTimeValue ? toTimeValue : null,
      },
    ];
    const payload = filterNoServiceTimeDays({ ...dayTimes, ...parseDayTime(v) });

    const newState = [
      {
        days: getDays(selectedDays),
        fromTime: fromTimeValue ? fromTimeValue : null,
        toTime: toTimeValue ? toTimeValue : null,
      },
    ];

    const mergedState = mergeDayTimeState(value || [], newState);

    onAdd(mergedState);
    addDayTime(payload);
  };

  const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setNoServiceCheckbox(e.target.checked);
    _onChange(null, 'all');
  };

  return (
    <Dialog
      isOpen={isVisible}
      customWidth="550px"
      dialogHeaderProps={{
        title: title,
        onClose: handleClose,
        iconType: 'edit-time',
      }}
      dialogActionProps={{
        buttons: [
          {
            label: t('common:cancel'),
            variant: 'outlined',
            onClick: () => setIsModalVisible(false),
          },
          {
            label: t('common:add'),
            variant: 'contained',
            onClick: () => _onAdd(),
            disabled: ((!fromTimeValue || !toTimeValue) && !noServiceCheckbox) || isLoading,
            isLoading: isLoading,
          },
        ],
      }}
    >
      <Box
        data-testid={dataTestId}
        sx={{ display: 'flex', gap: isSmallDevice ? 3 : 6.5, width: 'fit-content', margin: '0 auto' }}
      >
        {Object.keys(days).map((day, i) => (
          <RoundButton
            onClick={() => setSelectedDays({ ...selectedDays, [i]: !selectedDays[i] })}
            isSelected={selectedDays[i] === true}
            size={isSmallDevice ? RoundButtonSize.SM : RoundButtonSize.LG}
            key={`${day}-${i}`}
            aria-label={`${t(`common:day_of_week:${day}`)} - ${t(
              `day_time:edit:${selectedDays[i] === true ? 'selected' : 'unselected'}`,
            )}`}
          >
            {t(`common:day_of_week:${day}`)?.substring(0, 1)}
          </RoundButton>
        ))}
      </Box>
      <Box sx={{ mt: 6 }}>
        <FormGroup sx={{ '& .MuiFormControl-root': { flexGrow: 1 } }}>
          <FormControlLabel
            control={<Checkbox checked={noServiceCheckbox} onChange={handleCheckboxChange} />}
            label={t('day_time:select_day_time:no_service')}
            sx={{ ml: '4px' }}
          />
          {!noServiceCheckbox && (
            <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 4 }}>
              <TimePicker
                value={fromTimeValue}
                onChange={(v) => _onChange(v, 'fromTime')}
                label={t('day_time:select_day_time:from_time')}
                sx={{ width: '50%' }}
                maxTime={toTimeValue ? toTimeValue : undefined}
              />
              <TimePicker
                value={toTimeValue}
                onChange={(v) => _onChange(v, 'toTime')}
                label={t('day_time:select_day_time:to_time')}
                sx={{ width: '50%' }}
                minTime={fromTimeValue ? fromTimeValue : undefined}
              />
            </Box>
          )}
        </FormGroup>
      </Box>
    </Dialog>
  );
};
